import { Data, Router, ActivatedRouteSnapshot } from '@angular/router';
import { BreadcrumbItem } from 'nebula-ui-kit';

/**
 * Aplana el Ã¡rbol de rutas en el mismo orden que los segmentos de URL del navegador,
 * asociando a cada segmento el `data` de la ruta que lo definiÃ³ (incl. `data.breadcrumb`).
 */
function collectUrlSegmentsWithData(
  snap: ActivatedRouteSnapshot | null,
  acc: { path: string; data: Data }[] = [],
): { path: string; data: Data }[] {
  if (!snap) return acc;
  // Solo `data` declarado en esta ruta; evita mezclas con hijos.
  const ownData = (snap.routeConfig as { data?: Data } | null)?.data ?? {};
  for (const u of snap.url) {
    acc.push({ path: u.path, data: { ...ownData } });
  }
  if (snap.firstChild) {
    collectUrlSegmentsWithData(snap.firstChild, acc);
  }
  return acc;
}

/** Etiqueta cuando `data.breadcrumb` no aplica. Misma lÃ³gica histÃ³rica del layout. */
function getFallbackLabelForSegment(
  segment: string,
  index: number,
  startIndex: number,
  parts: string[],
): string {
  if (segment === 'vault' && index === startIndex) {
    return 'Nebula Vault';
  }
  if (segment === 'vault' && index === startIndex + 1 && parts[startIndex] === 'vault') {
    return 'Carga de Documentos';
  }

  const labels: Record<string, string> = {
    accounting: 'Contabilidad',
    tesorería: 'Tesorería',
    budget: 'Presupuesto',
    vault: 'Nebula Vault',
    masters: 'Maestros',
    'cost-center': 'Centros de Costo',
    'cost-center-type': 'Tipos de Centro de Costo',
    'department-city': 'Departamentos y Ciudades',
    list: 'Listado',
    create: 'Crear',
    edit: 'Editar',
    detail: 'Detalle',
    view: 'Ver',

    dashboard: 'Inicio',
    ingest: 'Carga de Documentos',
    preservation: 'Preservación Digital',
    retention: 'Retención Documental',
    'legal-hold': 'Custodia Legal ',
    reports: 'Reportes Generales',
    cloud: 'Respaldo Externo',
    notifications: 'Notificaciones',
    audit: 'Auditoría',
    settings: 'Configuración General',
    profile: 'Perfil',
    admin: 'Administrador',
    user: 'Gestión de Usuarios',
    users: 'Gestión de Usuarios',
    tenants: 'Gestión de Empresas',
    permissions: 'Permisos',
    servers: 'Configuración Servidores',
    'preservation-messages': 'Mensajes Informativos',
    'repository-admin': 'Bóveda Digital',
  };

  if (labels[segment]) return labels[segment];

  if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-/.test(segment)) {
    return 'Detalle';
  }

  return segment
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getLabelForSegment(
  segment: string,
  index: number,
  startIndex: number,
  parts: string[],
  routeData: Data | undefined,
): string {
  const fromData = routeData && routeData['breadcrumb'];
  if (typeof fromData === 'string' && fromData.trim() !== '') {
    return fromData;
  }
  return getFallbackLabelForSegment(segment, index, startIndex, parts);
}

export function getVaultParentCrumb(
  parts: string[],
  startIndex: number,
  getIconForLabel: (parentLabel: string) => string,
): BreadcrumbItem | null {
  if (parts[startIndex] !== 'vault') return null;
  const head = parts[startIndex + 1];
  if (!head) return null;

  if (head === 'dashboard') {
    return {
      label: 'Mi Tablero',
      route: '/layout/vault/dashboard',
      icon: getIconForLabel('Mi Tablero'),
    };
  }
  if (
    [
      'vault',
      'preservation',
      'retention',
      'legal-hold',
      'repository-admin',
      'reports',
      'cloud',
      'notifications',
    ].includes(head)
  ) {
    return {
      label: 'Centro Documental',
      route: '/layout/vault/vault',
      icon: getIconForLabel('Centro Documental'),
    };
  }
  if (['audit', 'settings', 'preservation-messages'].includes(head)) {
    return {
      label: 'Centro de control',
      route: '/layout/vault/audit',
      icon: getIconForLabel('Centro de control'),
    };
  }
  if (head === 'admin') {
    return {
      label: 'Administrador',
      route: '/layout/vault/admin/user',
      icon: getIconForLabel('Administrador'),
    };
  }
  return null;
}

export function dedupeConsecutiveBreadcrumbLabels(items: BreadcrumbItem[]): BreadcrumbItem[] {
  const result: BreadcrumbItem[] = [];
  for (const item of items) {
    const prev = result[result.length - 1];
    if (prev && prev.label.trim().toLowerCase() === item.label.trim().toLowerCase()) {
      continue;
    }
    result.push(item);
  }
  return result;
}

export interface BreadcrumbBuildOptions {
  /**
   * Icono del menÃº o pestaÃ±a activa para el Ãºltimo Ã­tem. Si se omite, se usa `getLastItemIconForPath` o "File".
   */
  lastMenuOrTabIcon?: string;
  /**
   * Resuelve el icono del Ãºltimo miga a partir de la ruta acumulada (p. ej. acierto con pestaÃ±as abiertas).
   */
  getLastItemIconForPath?: (accumulatedPath: string) => string | undefined;
  getIconForMenuLabel: (parentLabel: string) => string;
  /** Ruta mÃ­nima absoluta, sin ? ni #. Por defecto `router.url` normalizado. */
  urlOverride?: string;
}

/**
 * Construye `BreadcrumbItem[]` a partir de la URL y `data.breadcrumb` de cada nivel de ruta.
 */
function normalizePath(u: string): string {
  return u.split('?')[0].split('#')[0].split('/').filter((p) => p && p !== '').join('/');
}

/**
 * @param options.urlOverride â€” Si se pasa, los segmentos salen de esta ruta, pero el `data.breadcrumb`
 *   solo aplica si coincide con el `router.url` (misma ruta canÃ³nica); si no, solo heurÃ­stica.
 */
export function buildBreadcrumbItemsFromRouter(router: Router, options: BreadcrumbBuildOptions): BreadcrumbItem[] {
  const fromRouter = router.url.split('?')[0].split('#')[0];
  const clean = (options.urlOverride ?? fromRouter).split('?')[0].split('#')[0];
  const parts = clean.split('/').filter((p) => p && p !== '');

  const routerDataUsable =
    !options.urlOverride || normalizePath(fromRouter) === normalizePath(clean);

  const flat = routerDataUsable
    ? collectUrlSegmentsWithData(router.routerState.snapshot.root, [])
    : [];

  let startIndex = parts.indexOf('layout');
  if (startIndex !== -1) {
    startIndex += 1;
  } else {
    startIndex = 0;
  }

  if (startIndex >= parts.length) {
    return [];
  }

  const getIcon = options.getIconForMenuLabel;
  const breadcrumbItems: BreadcrumbItem[] = [];
  for (let i = startIndex; i < parts.length; i++) {
    const segment = parts[i]!;
    const pathParts = parts.slice(0, i + 1);
    const accumulatedPath = '/' + pathParts.join('/');

    const isLast = i === parts.length - 1;
    const routeData = routerDataUsable && i < flat.length ? flat[i]?.data : undefined;

    const label = getLabelForSegment(segment, i, startIndex, parts, routeData);
    const icon = isLast
      ? (options.getLastItemIconForPath?.(accumulatedPath) ?? options.lastMenuOrTabIcon ?? 'File')
      : 'Folder';

    breadcrumbItems.push({
      label,
      route: isLast ? undefined : accumulatedPath,
      icon: icon,
    });
  }

  const vaultParent = getVaultParentCrumb(parts, startIndex, getIcon);
  if (vaultParent && breadcrumbItems.length >= 2) {
    breadcrumbItems.splice(1, 0, vaultParent);
  }

  return dedupeConsecutiveBreadcrumbLabels(breadcrumbItems);
}
